/**
 * Format a price value to display in CLP format
 * @param {number|string} price - The price to format
 * @returns {string} Formatted price without decimal places
 */
export const formatPrice = (price) => {
  // Handle null, undefined or NaN values
  if (price === null || price === undefined || isNaN(Number(price))) {
    console.warn('Invalid price value:', price);
    return "0";
  }
  
  try {
    // Convert price to number if it's a string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    
    // Ensure we have a valid number after conversion
    if (isNaN(numericPrice)) {
      console.warn('Price conversion resulted in NaN:', price);
      return "0";
    }
    
    // Round to integer (no decimal places for CLP)
    const roundedPrice = Math.round(numericPrice);
    
    // Format with thousand separators
    return roundedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  } catch (error) {
    console.error('Error in formatPrice:', error);
    return "0";
  }
};

/**
 * Calculate discounted price
 * @param {number} originalPrice - The original price
 * @param {number} discountPercentage - The discount percentage
 * @returns {number} The price after discount
 */
export const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
  if (!originalPrice || isNaN(originalPrice) || originalPrice <= 0) return 0;
  if (!discountPercentage || isNaN(discountPercentage) || discountPercentage <= 0) return originalPrice;
  
  const discount = (originalPrice * discountPercentage) / 100;
  return originalPrice - discount;
};

export const getMinPrice = (blisters) => {
  if (blisters && blisters.length > 0) {
    return Math.min(...blisters.map(blister => parseFloat(blister.price)));
  }
  return 0;
};

export const getMaxPrice = (blisters) => {
  if (blisters && blisters.length > 0) {
    return Math.max(...blisters.map(blister => parseFloat(blister.price)));
  }
  return 0;
};

/**
 * Get the min and max price for a product with variants.
 * Uses absolute prices from variant options (effective_price or price).
 * @param {Object} product - The product object
 * @returns {Object} Object with min and max prices
 */
export const getProductPriceRange = (product) => {
  if (!product) return { min: 0, max: 0 };
  
  const basePrice = Number(product.effective_price || product.price || 0);
  
  if (!product.has_variants || !product.variants || !product.variants.length) {
    return { min: basePrice, max: basePrice };
  }
  
  const prices = [];
  product.variants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(option => {
        if (option.effective_price !== undefined && option.effective_price !== null) {
          prices.push(Number(option.effective_price));
        } else if (option.price !== undefined && option.price !== null) {
          prices.push(Number(option.price));
        }
      });
    }
  });

  if (prices.length === 0) {
    return { min: basePrice, max: basePrice };
  }

  return { min: Math.min(...prices), max: Math.max(...prices) };
};